import formatPrice, { stripHiddenPrice } from '../domain/format/formatPrice'
import type { PromotionType } from '../domain/types/graphql'
import type { IPromotionUiCatalog } from '../domain/types/promotions'

/**
 * Everything a card needs to know about the storefront it is being drawn for.
 *
 * The price fields mirror `formatPrice`'s own parameters rather than inventing a
 * second vocabulary: `currencySymbol` is its `currency`, `priceFormat` its
 * `format` mask, `priceDisplay` its `display` template. Leaving the last two
 * unset gives `formatPrice`'s defaults, which is what most storefronts run.
 */
export interface PresentationContext {
  currencySymbol: string
  /**
   * BCP 47 tag. `formatPrice` takes an explicit number mask rather than a
   * locale, so this is not consumed here — it is carried for the date and
   * quantity formatting the pages will need.
   */
  locale: string
  hidePrices: boolean
  placeholderImage: string
  /** `number-format.js` mask. Defaults to `formatPrice`'s `'#,##0.00'`. */
  priceFormat?: string
  currencyCode?: string
  /** Handlebars template, e.g. `'{{symbol}}{{price}}{{code}}'`. */
  priceDisplay?: string
}

/**
 * The badge kinds a card can draw, lowercased from the domain's `PromotionType`
 * so a component keys its styles off one convention. `unknown` covers a
 * promotion the domain labelled without attaching its data.
 */
export type PromotionKind = Lowercase<`${PromotionType}`> | 'unknown'

export interface PromotionBadge {
  text: string
  kind: PromotionKind
}

/**
 * The narrow slice of a catalog item this model reads.
 *
 * Deliberately not `IProduct`: that is a 50-field type, and depending on all of
 * it would mean every test needing a 50-field fixture. The page maps its query
 * result into this shape.
 */
export interface CatalogItemInput {
  sku: string
  name: string
  image: string
  price: number
  stock: number
  quantityInCart: number
  promotionUiDetails?: IPromotionUiCatalog | undefined
}

/**
 * Everything CatalogCard needs, fully resolved. No optional fields, no
 * computation left for the component. This is the contract that lets the
 * React and React Native cards be pure renders of the same data.
 */
export interface CatalogCardModel {
  sku: string
  title: string
  imageUrl: string
  priceText: string
  showPrice: boolean
  isOutOfStock: boolean
  canAddToCart: boolean
  quantityInCart: number
  promotionBadge: PromotionBadge | null
}

/**
 * `formatPrice` reports a hidden price as a sentinel token rather than an empty
 * string, so a caller can drop the whole decorated price instead of rendering
 * `$MXN`. View-models consume the sentinel here and hand the component a plain
 * string, which is why the flag is passed through instead of short-circuited:
 * what "hidden" renders as stays a domain decision.
 */
export const toPriceText = (price: number, ctx: PresentationContext): string =>
  stripHiddenPrice(
    formatPrice({
      price,
      currency: ctx.currencySymbol,
      hidePrices: ctx.hidePrices,
      ...(ctx.priceFormat !== undefined ? { format: ctx.priceFormat } : {}),
      ...(ctx.currencyCode !== undefined ? { code: ctx.currencyCode } : {}),
      ...(ctx.priceDisplay !== undefined ? { display: ctx.priceDisplay } : {}),
    }),
  )

export const toPromotionBadge = (
  promotionUiDetails: IPromotionUiCatalog | undefined,
): PromotionBadge | null => {
  const label = promotionUiDetails?.label
  if (label == null || label === '') return null
  const type = promotionUiDetails?.data?.type
  return {
    text: label,
    kind: type ? (type.toLowerCase() as PromotionKind) : 'unknown',
  }
}

export const toCatalogCardModel = (
  item: CatalogItemInput,
  ctx: PresentationContext,
): CatalogCardModel => {
  const isOutOfStock = item.stock <= 0

  return {
    sku: item.sku,
    title: item.name,
    imageUrl: item.image || ctx.placeholderImage,
    priceText: toPriceText(item.price, ctx),
    showPrice: !ctx.hidePrices,
    isOutOfStock,
    canAddToCart: !isOutOfStock,
    quantityInCart: item.quantityInCart,
    promotionBadge: toPromotionBadge(item.promotionUiDetails),
  }
}
